import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ControlsOf,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { ControlState } from '@ngneat/reactive-forms/lib/core';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Generator } from '@digital-platform/generators/domain';
import {
  offSetFromTop,
  scrollOnError,
} from '@digital-platform/ries-hub-shared/services';
import { Billing } from '@digital-platform/shared/domain';
import { Signature } from '@digital-platform/sign-now/domain';
import { User } from '@digital-platform/users/domain';
import {
  InternalStatus,
  PhysicalComponent,
  RegulatoryQuestions,
  SpecialWasteProfile,
  WasteProfileStatus,
  minAndMaxDate,
  resetBadRawDateInput,
} from '@digital-platform/waste-profiles/domain';

type FormData = Pick<
  SpecialWasteProfile,
  | 'approvedDate'
  | 'expiryDate'
  | 'submittedDate'
  | 'coordinator'
  | 'approverName'
  | 'approvalNotes'
  | 'internalStatus'
  | 'status'
  | 'generator'
  | 'physicalComponent'
  | 'regulatoryQuestions'
  | 'signatures'
> & {
  salesRep: {
    name: string;
    number: string;
  };
  destination: SpecialWasteProfile;
  wasteStream: SpecialWasteProfile;
  billing: SpecialWasteProfile['generator']['billing'];
};

type FormControls = ControlsOf<
  Omit<
    FormData,
    | 'approvedDate'
    | 'expiryDate'
    | 'submittedDate'
    | 'coordinator'
    | 'salesRep'
    | 'generator'
    | 'physicalComponent'
    | 'regulatoryQuestions'
    | 'destination'
    | 'billing'
    | 'wasteStream'
  >
> & {
  coordinator: FormControl<User>;
  salesRep: FormControl<{ name: string; number: string }>;
  approvedDate: FormControl<Date>;
  expiryDate: FormControl<Date>;
  submittedDate: FormControl<Date>;
  generator: FormControl<Generator>;
  physicalComponent: FormControl<PhysicalComponent>;
  regulatoryQuestions: FormControl<RegulatoryQuestions>;
  destination: FormControl<SpecialWasteProfile>;
  wasteStream: FormControl<SpecialWasteProfile>;
  billing: FormControl<Billing>;
};

interface SignatureForm {
  company: FormControl<string>;
  date: FormControl<Date>;
  email: FormControl<string>;
  isSigned: FormControl<boolean>;
  name: FormControl<string>;
  title: FormControl<string>;
}

enum WasteProfileSignatureType {
  CHANGE_REQUEST = 'Change Request',
  RECERTIFICATION = 'Recertification',
  WASTE_PROFILE = 'Waste Profile',
}

enum WasteProfilePdfType {
  SIGN_NOW = 'Sign Now',
  DOCUSIGN = 'Docu Sign',
}

@Component({
  selector: 'digital-platform-special-waste-manual-edit',
  templateUrl: './special-waste-manual-edit.component.html',
  styleUrls: ['./special-waste-manual-edit.component.scss'],
})
export class SpecialWasteManualEditComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @Input() wasteProfile: SpecialWasteProfile;
  @Input() disableButtons;
  @Output() public formDataChanged = new EventEmitter<FormData>();
  @Output() public formStatus = new EventEmitter<ControlState>();
  @Output() public next = new EventEmitter<void>();
  @Output() public emitSignature = new EventEmitter();
  private destroyed$ = new Subject<void>();
  profileStatus?: string = null;

  public form: FormGroup<FormControls>;
  public selectedSignature: WasteProfileSignatureType;
  public selectedSignatureType: WasteProfilePdfType;
  internalStatusOptions = {
    options: [
      InternalStatus.RECEIVED,
      InternalStatus.COORDINATOR_REVIEW,
      InternalStatus.COORDINATOR_APPROVED,
      InternalStatus.ANALYST_REVIEW,
      InternalStatus.ANALYST_APPROVED,
      InternalStatus.FINAL_APPROVAL,
      InternalStatus.PENDING_CUSTOMER_INFO,
      InternalStatus.REJECTED,
    ],
  };
  statusOptions = {
    options: [
      WasteProfileStatus.NOT_SUBMITTED,
      WasteProfileStatus.SUBMITTED,
      WasteProfileStatus.ACTION_REQUIRED,
      WasteProfileStatus.APPROVED,
      WasteProfileStatus.EXPIRING,
      WasteProfileStatus.EXPIRED,
      WasteProfileStatus.INCOMPLETE,
      WasteProfileStatus.PENDING_SIGNATURE,
    ],
  };
  public isLegacy = false;
  constructor(
    private formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly el: ElementRef
  ) {}

  // Imported from shared location to avoid duplication
  minAndMaxDate = minAndMaxDate;
  resetBadRawDateInput = resetBadRawDateInput;

  ngAfterViewChecked(): void {
    this.profileStatus = this.form.getRawValue().status;
  }

  initializeForm(formValue: SpecialWasteProfile) {
    this.isLegacy =
      formValue?.isLegacyProfile !== undefined
        ? formValue.isLegacyProfile
        : false;

    this.form = this.formBuilder.group({
      approvedDate: [formValue?.approvedDate, this.minAndMaxDate()],
      expiryDate: [formValue?.expiryDate, this.minAndMaxDate()],
      submittedDate: [formValue?.submittedDate, this.minAndMaxDate()],
      salesRep: [
        {
          name: formValue?.salesRepName,
          number: formValue?.salesRepNumber,
        },
      ],
      coordinator: [formValue?.coordinator],
      approverName: [formValue?.approverName],
      approvalNotes: [formValue?.approvalNotes],
      customerName:
        this.isLegacy === false
          ? [formValue?.customerName, [Validators.required]]
          : [formValue?.customerName],
      internalStatus: [formValue?.internalStatus],
      status: [formValue?.status],
      generator: [formValue?.generator],
      billing: [formValue?.generator?.billing],
      wasteStream: [formValue],
      destination: [formValue],
      physicalComponent: [formValue?.physicalComponent],
      regulatoryQuestions: [formValue?.regulatoryQuestions],
      signatures: this.formBuilder.array(
        this.createSignaturesFormArray(this.getLatestSignatures(formValue))
      ),
    });
  }
  ngOnInit(): void {
    this.initializeForm(this.wasteProfile);
    this.watchFormChanges();
    this.form.controls.status.valueChanges.subscribe((status) => {
      this.profileStatus = status;
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getLatestSignatures = (wasteProfile: SpecialWasteProfile) => {
    const changeRequestSignature = wasteProfile?.pendingChangeRequest;
    const recertifySignature = wasteProfile?.pendingRecertification;
    if (
      wasteProfile?.docusignData?.envelopeId ||
      changeRequestSignature?.docusignData?.envelopeId ||
      recertifySignature?.docusignData?.envelopeId
    ) {
      if (!changeRequestSignature && !recertifySignature) {
        this.selectedSignature = WasteProfileSignatureType.WASTE_PROFILE;
        this.selectedSignatureType = WasteProfilePdfType.DOCUSIGN;
        return wasteProfile?.docusignData?.signatures;
      } else if (changeRequestSignature && recertifySignature) {
        this.selectedSignature =
          _.first(changeRequestSignature?.docusignData?.signatures)?.date >
          _.first(recertifySignature?.docusignData?.signatures)?.date
            ? WasteProfileSignatureType.CHANGE_REQUEST
            : WasteProfileSignatureType.RECERTIFICATION;
        this.selectedSignatureType = WasteProfilePdfType.DOCUSIGN;
        return this.selectedSignature ===
          WasteProfileSignatureType.CHANGE_REQUEST
          ? changeRequestSignature?.docusignData?.signatures
          : recertifySignature?.docusignData?.signatures;
      } else if (changeRequestSignature?.docusignData) {
        this.selectedSignature = WasteProfileSignatureType.CHANGE_REQUEST;
        this.selectedSignatureType = WasteProfilePdfType.DOCUSIGN;
        return changeRequestSignature?.docusignData?.signatures;
      } else {
        this.selectedSignature = WasteProfileSignatureType.RECERTIFICATION;
        this.selectedSignatureType = WasteProfilePdfType.DOCUSIGN;
        return recertifySignature?.docusignData?.signatures;
      }
    } else {
      if (!changeRequestSignature && !recertifySignature) {
        this.selectedSignature = WasteProfileSignatureType.WASTE_PROFILE;
        this.selectedSignatureType = WasteProfilePdfType.SIGN_NOW;
        return wasteProfile?.signNowDocument?.signatures;
      } else if (changeRequestSignature && recertifySignature) {
        this.selectedSignature =
          _.first(changeRequestSignature?.signNowDocument?.signatures)?.date >
          _.first(recertifySignature?.signNowDocument?.signatures)?.date
            ? WasteProfileSignatureType.CHANGE_REQUEST
            : WasteProfileSignatureType.RECERTIFICATION;
        this.selectedSignatureType = WasteProfilePdfType.SIGN_NOW;
        return this.selectedSignature ===
          WasteProfileSignatureType.CHANGE_REQUEST
          ? changeRequestSignature?.signNowDocument?.signatures
          : recertifySignature?.signNowDocument?.signatures;
      } else if (changeRequestSignature?.signNowDocument) {
        this.selectedSignature = WasteProfileSignatureType.CHANGE_REQUEST;
        this.selectedSignatureType = WasteProfilePdfType.SIGN_NOW;
        return changeRequestSignature?.signNowDocument?.signatures;
      } else {
        this.selectedSignature = WasteProfileSignatureType.RECERTIFICATION;
        this.selectedSignatureType = WasteProfilePdfType.SIGN_NOW;
        return recertifySignature?.signNowDocument?.signatures;
      }
    }
  };

  watchFormChanges() {
    this.form.value$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      // form.status == invalid => markAllCheck()

      this.formDataChanged.emit(value);
    });
    this.form.status$.pipe(takeUntil(this.destroyed$)).subscribe((status) => {
      this.formStatus.emit(status);
    });
  }

  private scrollOnError() {
    let formControl: string;
    const generatorErrors = Object.keys(
      this.form.controls?.generator?.errors || []
    );

    const billingErrors = Object.keys(
      this.form.controls?.billing?.errors || []
    );

    const wasteStreamErrors = Object.keys(
      this.form.controls?.wasteStream?.errors || []
    );

    const destinationErrors = Object.keys(
      this.form.controls?.destination?.errors || []
    );
    const physicalComponentErrors = Object.keys(
      this.form.controls?.physicalComponent?.errors || []
    );
    const regulatoryQuestionsErrors = Object.keys(
      this.form.controls?.regulatoryQuestions?.errors || []
    );

    switch (true) {
      case generatorErrors.length > 0:
        scrollOnError(this.el, generatorErrors);
        break;
      case billingErrors.length > 0:
        formControl = 'billing';
        break;
      case wasteStreamErrors.length > 0:
        scrollOnError(this.el, wasteStreamErrors);
        break;
      case destinationErrors.length > 0:
        scrollOnError(this.el, destinationErrors);
        break;
      case physicalComponentErrors.length > 0:
        formControl = 'physicalComponent';
        break;
      case regulatoryQuestionsErrors.length > 0:
        formControl = 'regulatoryQuestions';
        break;
      default:
        break;
    }
    const invalidControl = this.el.nativeElement.querySelector(
      `[formControlName=${formControl}]`
    );

    window.scroll({
      top: offSetFromTop(invalidControl),
      left: 0,
      behavior: 'smooth',
    });
  }

  onBack(): void {
    this.router.navigate(['/search-waste-profile']);
  }

  onNext(): void {
    if (
      this.form.invalid &&
      this.form.controls.status.value !== 'Not Submitted'
    ) {
      this.scrollOnError();
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      return;
    }
    this.emitSignature.emit({
      selectedWasteProfile: this.selectedSignature,
      selectedSignatureType: this.selectedSignatureType,
    });
    this.next.emit();
  }

  createSignaturesFormArray(signatures: Signature[]) {
    const formGroups = [];
    signatures?.forEach((signature: Signature) => {
      formGroups.push(this.createSignatureFormGroup(signature));
    });
    return formGroups;
  }

  createSignatureFormGroup(value: Signature): FormGroup<SignatureForm> {
    return this.formBuilder.group({
      company: [value?.company],
      date: [new Date(value?.date)],
      email: [value?.email, Validators.email],
      isSigned: [value?.isSigned],
      name: [value?.name],
      title: [value?.title],
    });
  }

  signatureErrorHandling = (control: string, error: string, i: number) => {
    const sign = this.form.controls.signatures as any;
    return (
      sign.controls[i].touched &&
      (sign.controls[i] as any).controls[control].hasError(error)
    );
  };

  public errorHandling = (control: string, error: string) =>
    this.form.controls[control].touched &&
    this.form.controls[control].hasError(error);
}
